import { useId, useRef, useState } from 'react';
import { Power, X } from 'lucide-react';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// The «slå av meldinger» switch for the club portal (full view + dock). The
// identity here is the club; the member app has its own copy of this in the
// Meldinger tab, and the court pad in «Kontakt senteret».

export interface MessagingSwitchProps {
  disabled: boolean;
  /** Who is switched off — «klubben», «denne banen» … (bokmål, lower case). */
  subject: string;
  onSet: (disabled: boolean) => Promise<unknown> | void;
  /** Compact = the dock (no page-level card). */
  compact?: boolean;
}

/** The «off» state card with the way back on. */
export function MessagingOffCard({
  subject,
  onSet,
  compact = false,
}: Pick<MessagingSwitchProps, 'subject' | 'onSet' | 'compact'>) {
  return (
    <div className={`text-center ${compact ? 'p-5' : 'card p-8'}`} data-testid="messaging-disabled">
      <Power size={24} className="mx-auto mb-2 text-ink-4" />
      <div className="font-display text-lg font-semibold text-ink mb-1">
        Meldinger er slått av for {subject}
      </div>
      <p className="text-sm text-ink-2 max-w-sm mx-auto">
        Ingen meldinger sendes eller mottas, og {subject} vises ikke for andre i meldinger. Slå på
        igjen når du vil.
      </p>
      <button onClick={() => void onSet(false)} className="btn-primary text-sm mt-4">
        Slå på meldinger
      </button>
    </div>
  );
}

/** The small «slå av» link with its confirmation. */
export function MessagingOffLink({
  subject,
  onSet,
}: Pick<MessagingSwitchProps, 'subject' | 'onSet'>) {
  const [confirm, setConfirm] = useState(false);
  const uid = safeId('messaging-off', useId());
  const ids = { title: `${uid}-title`, body: `${uid}-body` };
  // Focus starts on «Avbryt»: turning messaging off is the consequential
  // choice, so the safe one is where Enter lands.
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <button
        onClick={() => setConfirm(true)}
        className="inline-flex items-center gap-1.5 text-xs text-ink-3 underline"
      >
        <Power size={12} /> Slå av meldinger for {subject}
      </button>
      {confirm && (
        // A `Dialog` (#900). The role used to sit on the BACKDROP, so the
        // surface you click to leave was part of the «modal», and nothing kept
        // focus inside. Now the panel is the dialog, named by its own title;
        // Escape is «Avbryt», and focus goes back to the link.
        <Dialog
          labelledBy={ids.title}
          describedBy={ids.body}
          onClose={() => setConfirm(false)}
          showClose={false}
          initialFocus={cancelRef}
          backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4"
          backdropStyle={{ background: 'rgba(15,22,17,0.45)' }}
          className="card-elev w-full max-w-md p-5 max-h-[92vh] overflow-y-auto"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <div className="flex items-start justify-between mb-1">
            <h2 id={ids.title} className="font-display text-xl font-semibold">
              Slå av meldinger?
            </h2>
            <button
              type="button"
              onClick={() => setConfirm(false)}
              aria-label="Lukk"
              className="btn-ghost p-1"
            >
              <X size={16} />
            </button>
          </div>
          <p id={ids.body} className="text-sm text-ink-2 mb-4">
            {subject.charAt(0).toUpperCase() + subject.slice(1)} vil ikke sende eller motta
            meldinger, og vises ikke for andre i meldinger. Du kan slå på igjen fra samme sted.
          </p>
          <div className="flex justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={() => setConfirm(false)}
              className="btn-outline text-sm"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={async () => {
                await onSet(true);
                setConfirm(false);
              }}
              className="btn-primary text-sm"
            >
              Slå av meldinger
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
